import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { useTheme } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import HomeIcon from "@mui/icons-material/Home";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LinkIcon from "@mui/icons-material/Link";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import EmailIcon from "@mui/icons-material/Email";
import Box from "@mui/material/Box";
import {
  setLastWorkedTicket,
  setEditingOnTicket,
  pushTicketToHistory,
  setCurrentRow,
  toggleTagModal,
  toggleInfoModal,
  toggleEmailModal,
} from "../../../../actions/projectmail";
import { useDefaultPasscodeStyles } from "../../../../constants/passCodeUtils";
import { EVENTMGT_ORDER_SERVER } from "../../../../constants/constants";

const tooltipSlotProps = {
  tooltip: {
    sx: {
      fontSize: "0.7rem",
      m: 0,
    },
  },
};

const TableLayoutActionsComponent = ({
  row,
  setOpenModal,
  translate,
  lastWorkedTicket,
  setLastWorkedTicketFn,
  setEditingOnTicketFn,
  username,
  pushTicketToHistoryFn,
  setCurrentRowFn,
  type,
  openTagModalFn,
  openInfoModalFn,
  openEmailModalFn,
}) => {
  const theme = useTheme();
  const pc = useDefaultPasscodeStyles();
  const s = useMemo(
    () => ({
      field: { px: 2 },
      actions: {
        display: "flex",
        maxWidth: "100%",
        flexWrap: "nowrap",
      },
      edit: {
        p: 0.5,
        ml: 0.5,
        mr: 0.5,
      },
      greenLine: {
        width: theme.spacing(0.5),
        mr: 1.5,
        ml: -2,
        backgroundColor: "#66cc33",
        minHeight: "100%",
      },
      placeholder: {
        minWidth: theme.spacing(4),
        height: theme.spacing(3),
        m: theme.spacing(0, 0.5),
        p: theme.spacing(0, 0.5),
      },
    }),
    [theme]
  );

  const url = () => {
    if (row.orderType === "webcast") return `${EVENTMGT_ORDER_SERVER}/order/myubi/index.xhtml?filter=${row.orderID}`;
    if (row.orderType === "event") return `${EVENTMGT_ORDER_SERVER}/order/ae/index.xhtml?filter=${row.orderID}`;
    return undefined;
  };

  const home = () => {
    if (row.orderID !== -1 && url() !== undefined && type !== "closed" && type !== "merged") {
      return (
        <Tooltip title={translate.home2} enterDelay={500} placement="top" slotProps={tooltipSlotProps}>
          <IconButton size="small" sx={[s.edit, pc.textField]} onClick={() => window.open(url())}>
            <HomeIcon />
          </IconButton>
        </Tooltip>
      );
    }
    if (type !== "closed" && type !== "merged") {
      return <Box sx={s.placeholder} />;
    }
    return null;
  };
  return (
    <TableCell onClick={() => setLastWorkedTicketFn(row.id)} sx={s.field} padding="none">
      <Box sx={s.actions}>
        {lastWorkedTicket === row.id && <Box sx={s.greenLine} />}
        <Tooltip title={translate.info} enterDelay={500} placement="top" slotProps={tooltipSlotProps}>
          <IconButton
            sx={[s.edit, pc.textField]}
            size="small"
            onClick={() => {
              setCurrentRowFn(row);
              pushTicketToHistoryFn(row);
              openInfoModalFn();
            }}
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={translate.link} enterDelay={500} placement="top" slotProps={tooltipSlotProps}>
          <IconButton
            size="small"
            sx={[s.edit, pc.textField]}
            onClick={() => {
              window.open("https://zammad.ubivent.net/#ticket/zoom/" + row.id, "zammad");
              pushTicketToHistoryFn(row);
            }}
          >
            <LinkIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={translate.linkWithAssignment} enterDelay={500} placement="top" slotProps={tooltipSlotProps}>
          <IconButton
            size="small"
            sx={[s.edit, pc.textField]}
            onClick={() => {
              if (row.owner !== "" && row.owner !== "-" && row.owner !== username) setOpenModal(true);
              else {
                setEditingOnTicketFn(row.id);
                window.open("https://zammad.ubivent.net/#ticket/zoom/" + row.id, "zammad");
                pushTicketToHistoryFn(row);
              }
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={translate.setTags} enterDelay={500} placement="top" slotProps={tooltipSlotProps}>
          <IconButton
            size="small"
            sx={[s.edit, pc.textField]}
            onClick={() => {
              setCurrentRowFn(row);
              openTagModalFn();
              pushTicketToHistoryFn(row);
            }}
          >
            <LocalOfferIcon />
          </IconButton>
        </Tooltip>
        <div>{home()}</div>
        <Tooltip title={translate.mailPreview} enterDelay={500} placement="top" slotProps={tooltipSlotProps}>
          <IconButton
            size="small"
            sx={[s.edit, pc.textField]}
            onClick={() => {
              setCurrentRowFn(row);
              openEmailModalFn();
            }}
          >
            <EmailIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  );
};

TableLayoutActionsComponent.propTypes = {
  row: PropTypes.object.isRequired,
  translate: PropTypes.object.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  setLastWorkedTicketFn: PropTypes.func.isRequired,
  setEditingOnTicketFn: PropTypes.func.isRequired,
  lastWorkedTicket: PropTypes.number.isRequired,
  username: PropTypes.string.isRequired,
  pushTicketToHistoryFn: PropTypes.func.isRequired,
  setCurrentRowFn: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  openTagModalFn: PropTypes.func.isRequired,
  openInfoModalFn: PropTypes.func.isRequired,
  openEmailModalFn: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  translate: state.ui.translate,
  lastWorkedTicket: state.projectmail.lastWorkedTicket,
  username: state.oauth.username,
});

const mapDispatchToProps = dispatch => ({
  setLastWorkedTicketFn: id => dispatch(setLastWorkedTicket(id)),
  setEditingOnTicketFn: id => dispatch(setEditingOnTicket(id, true)),
  pushTicketToHistoryFn: ticket => dispatch(pushTicketToHistory(ticket)),
  setCurrentRowFn: row => dispatch(setCurrentRow(row)),
  openTagModalFn: () => dispatch(toggleTagModal(true)),
  openInfoModalFn: () => dispatch(toggleInfoModal(true)),
  openEmailModalFn: () => dispatch(toggleEmailModal(true)),
});

const TableLayoutActions = connect(
  mapStateToProps,
  mapDispatchToProps
)(TableLayoutActionsComponent);

export default TableLayoutActions;
